/**
 * Catálogo da linguagem de condição (`GET /tipos/formulario/condicao`) e helpers de consulta.
 *
 * O catálogo é a fonte de verdade para variáveis, sinais e operadores por tipo; `CATALOGO_PADRAO`
 * é um espelho dos enums do backend usado só quando a API ainda não respondeu (e pelos testes de
 * paridade, que não têm rede). Quem decide sempre é o servidor.
 */
import axios, { AxiosResponse } from 'axios'
import { Operador, TipoCampoCondicao, TipoValor, tipoValorDoCampo } from './tipos'

const API_URL = process.env.REACT_APP_API_URL

export type ValorPermitido = {
  valor: string
  rotulo: string
}

export type VariavelCatalogo = {
  codigo: string
  rotulo: string
  tipo: TipoValor
  /** Lista vazia = domínio livre (ex.: idade). */
  valoresPermitidos: ValorPermitido[]
}

export type SinalCatalogo = {
  codigo: string
  descricao: string
  unidade: string
  casasDecimais: number
}

export type OperadorCatalogo = {
  /** Token ASCII gravado no JSON. */
  json: Operador
  /** Rótulo de tela (≠ ≥ ≤). */
  simbolo: string
  /** Tipos de referência que aceitam o operador. */
  tipos: Array<'NUMERO' | 'TEXTO' | 'LISTA'>
  /** `true` em `existe`/`nao_existe`, que não levam a chave `valor`. */
  semValor: boolean
}

export type CondicaoCatalogo = {
  variaveis: VariavelCatalogo[]
  sinais: SinalCatalogo[]
  operadores: OperadorCatalogo[]
}

export const CATALOGO_VAZIO: CondicaoCatalogo = { variaveis: [], sinais: [], operadores: [] }

export const getCatalogoCondicao = (): Promise<CondicaoCatalogo> =>
  axios
    .get(`${API_URL}/tipos/formulario/condicao`)
    .then((res: AxiosResponse<CondicaoCatalogo>) => res.data)

const op = (
  json: Operador,
  simbolo: string,
  tipos: Array<TipoValor>,
  semValor = false
): OperadorCatalogo => ({ json, simbolo, tipos, semValor })

/** Espelho dos enums `VariavelCatalogo`, `SinalVital` e `Operador` do backend (T8a). */
export const CATALOGO_PADRAO: CondicaoCatalogo = {
  variaveis: [
    {
      codigo: 'atendimento.tipo',
      rotulo: 'Tipo de atendimento',
      tipo: 'TEXTO',
      valoresPermitidos: [
        { valor: 'PA', rotulo: 'Pronto Atendimento' },
        { valor: 'ASO', rotulo: 'ASO' },
        { valor: 'SC', rotulo: 'Screening' },
        { valor: 'TK', rotulo: 'Telekit' },
        { valor: 'CA', rotulo: 'Consulta Agendada' },
        { valor: 'IC', rotulo: 'Interconsulta' },
      ],
    },
    {
      codigo: 'paciente.sexo',
      rotulo: 'Sexo do paciente',
      tipo: 'TEXTO',
      valoresPermitidos: [
        { valor: 'F', rotulo: 'Feminino' },
        { valor: 'M', rotulo: 'Masculino' },
      ],
    },
    {
      codigo: 'paciente.idade_anos',
      rotulo: 'Idade (anos completos)',
      tipo: 'NUMERO',
      valoresPermitidos: [],
    },
    {
      codigo: 'paciente.idade_meses',
      rotulo: 'Idade (meses completos)',
      tipo: 'NUMERO',
      valoresPermitidos: [],
    },
  ],
  sinais: [
    { codigo: 'SPO2', descricao: 'Saturação de oxigênio', unidade: '%', casasDecimais: 0 },
    { codigo: 'FC', descricao: 'Frequência cardíaca', unidade: 'bpm', casasDecimais: 0 },
    { codigo: 'PAS', descricao: 'Pressão arterial sistólica', unidade: 'mmHg', casasDecimais: 0 },
    { codigo: 'TEMP', descricao: 'Temperatura', unidade: '°C', casasDecimais: 1 },
    { codigo: 'FR', descricao: 'Frequência respiratória', unidade: 'irpm', casasDecimais: 0 },
    { codigo: 'GLICEMIA', descricao: 'Glicemia capilar', unidade: 'mg/dL', casasDecimais: 0 },
  ],
  operadores: [
    op('=', '=', ['NUMERO', 'TEXTO', 'LISTA']),
    op('!=', '≠', ['NUMERO', 'TEXTO', 'LISTA']),
    op('>', '>', ['NUMERO']),
    op('>=', '≥', ['NUMERO']),
    op('<', '<', ['NUMERO']),
    op('<=', '≤', ['NUMERO']),
    op('entre', 'entre', ['NUMERO']),
    op('em', 'em', ['NUMERO', 'TEXTO', 'LISTA']),
    op('existe', 'existe', ['NUMERO', 'TEXTO', 'LISTA'], true),
    op('nao_existe', 'não existe', ['NUMERO', 'TEXTO', 'LISTA'], true),
  ],
}

export const variavelPorCodigo = (
  catalogo: CondicaoCatalogo,
  codigo: string
): VariavelCatalogo | undefined => catalogo.variaveis.find((v) => v.codigo === codigo)

export const sinalPorCodigo = (
  catalogo: CondicaoCatalogo,
  codigo: string
): SinalCatalogo | undefined => catalogo.sinais.find((s) => s.codigo === codigo)

/**
 * Categoria de operador aceita por uma referência: lista (campo múltiplo) ou o tipo semântico.
 */
export const categoriaDaReferencia = (
  tipo: TipoValor,
  multiplo: boolean
): 'NUMERO' | 'TEXTO' | 'LISTA' => (multiplo ? 'LISTA' : tipo)

export const operadoresPara = (
  catalogo: CondicaoCatalogo,
  tipo: TipoValor,
  multiplo: boolean
): OperadorCatalogo[] => {
  const categoria = categoriaDaReferencia(tipo, multiplo)
  return catalogo.operadores.filter((o) => o?.tipos?.includes(categoria))
}

/** Operadores compatíveis com um campo do formulário; vazio quando o tipo não é referenciável. */
export const operadoresParaCampo = (
  catalogo: CondicaoCatalogo,
  tipo: TipoCampoCondicao,
  multiplo: boolean
): OperadorCatalogo[] => {
  const tipoValor = tipoValorDoCampo(tipo)
  return tipoValor ? operadoresPara(catalogo, tipoValor, multiplo) : []
}
